import { tidyPhrase, containsPhrase, tagSubGroup } from "./keywords";

// Scans from a niche we don't have yet run under this one.
export const UNSORTED_NICHE_NAME = "Unsorted";

// Points for each kind of match. A niche needs at least MIN_NICHE_SCORE,
// so trigger words alone ("emergency") never pick a niche.
const PHRASE_POINTS_PER_WORD = 2;
const CLOSE_SPELLING_POINTS = 1;
const TRIGGER_POINTS = 0.5;
const MIN_NICHE_SCORE = 1;

// Edit distance: how many letters must change to turn a into b.
export function editDistance(a, b) {
  const prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let diagonal = prev[0];
    prev[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const above = prev[j];
      prev[j] = Math.min(prev[j] + 1, prev[j - 1] + 1, diagonal + (a[i - 1] === b[j - 1] ? 0 : 1));
      diagonal = above;
    }
  }
  return prev[b.length];
}

// "plumer" ~ "plumber": one letter out for words of 5-7 letters, two for 8+.
// Shorter words must match exactly, and the first letter must always match
// (so "climbing" isn't taken for "plumbing").
export function isCloseSpelling(a, b) {
  if (a === b) return true;
  const shorter = Math.min(a.length, b.length);
  if (shorter < 5 || a[0] !== b[0]) return false;
  const allowed = shorter >= 8 ? 2 : 1;
  return Math.abs(a.length - b.length) <= allowed && editDistance(a, b) <= allowed;
}

// Every phrase that means this niche: its name ("Heating & Boilers" also
// gives "heating" and "boiler") and its synonyms, all tidied.
export function nicheTerms(niche) {
  const names = [niche.name, ...String(niche.name).split(/\s*(?:&|\/|,)\s*/)];
  const terms = [...names, ...(niche.synonyms || [])].map(tidyPhrase).filter(Boolean);
  return [...new Set(terms)];
}

// Niches the search page may offer (not Unsorted, not switched off).
export function selectableNiches(niches) {
  return (niches || []).filter(n => n.active !== false && n.name !== UNSORTED_NICHE_NAME);
}

// How strongly one tidied keyword points at one niche.
function keywordNicheScore(standardForm, niche) {
  const words = standardForm.split(" ");
  let best = 0;
  for (const term of nicheTerms(niche)) {
    if (containsPhrase(standardForm, term)) {
      best = Math.max(best, term.split(" ").length * PHRASE_POINTS_PER_WORD);
    } else if (!term.includes(" ") && words.some(w => isCloseSpelling(w, term))) {
      best = Math.max(best, CLOSE_SPELLING_POINTS);
    }
  }
  const triggerHit = (niche.sub_groups || []).some(g =>
    (g.trigger_words || []).some(t => containsPhrase(standardForm, tidyPhrase(t)))
  );
  return best + (triggerHit ? TRIGGER_POINTS : 0);
}

// Works out the niche from the keywords, e.g. "emergency plumber" +
// "plumber near me" -> Plumbing, sub-group Emergency.
// keywords: tidied standard forms. niches: each with sub_groups.
// Returns { niche, subGroup, keywordSubGroups } or null when nothing matches.
// keywordSubGroups[i] is the sub-group for keywords[i] (or null).
export function matchNicheFromKeywords(keywords, niches) {
  let bestNiche = null;
  let bestScore = 0;
  for (const niche of selectableNiches(niches)) {
    const score = keywords.reduce((sum, kw) => sum + keywordNicheScore(kw, niche), 0);
    if (score > bestScore) {
      bestNiche = niche;
      bestScore = score;
    }
  }
  if (!bestNiche || bestScore < MIN_NICHE_SCORE) return null;

  const keywordSubGroups = keywords.map(kw => tagSubGroup(kw, bestNiche.sub_groups));
  return { niche: bestNiche, subGroup: mostCommonSubGroup(keywordSubGroups), keywordSubGroups };
}

// The niches Change may offer: those at least one keyword fits by name,
// synonym or close spelling ("plumber" + "boiler repair" -> Plumbing or
// Heating & Boilers, never Accountants). If no keyword fits any niche,
// every niche is offered.
export function nichesFittingKeywords(keywords, niches) {
  const selectable = selectableNiches(niches);
  const fitting = selectable.filter(niche =>
    keywords.some(kw => keywordNicheScore(kw, niche) >= MIN_NICHE_SCORE)
  );
  return fitting.length > 0 ? fitting : selectable;
}

// The sub-group most keywords share; ties go to the earliest keyword. Null if none.
export function mostCommonSubGroup(subGroups) {
  const counts = new Map();
  for (const group of subGroups) {
    if (group) counts.set(group.id, { group, count: (counts.get(group.id)?.count || 0) + 1 });
  }
  let best = null;
  for (const entry of counts.values()) {
    if (!best || entry.count > best.count) best = entry;
  }
  return best ? best.group : null;
}

// The "Change" box: niches whose name or synonyms match what was typed,
// allowing close spellings ("plumer" -> Plumbing, "orthodontist" -> Dental).
// Best matches first.
export function searchNiches(query, niches, limit = 8) {
  const q = tidyPhrase(query);
  if (!q) return selectableNiches(niches).slice(0, limit);
  return scoreNiches(q, niches).slice(0, limit).map(s => s.niche);
}

// Scores for the Change box: 4 = exactly a name/synonym, 3 = starts with or
// contains one, 2 = part of one, 1 = only a single word looks alike
// ("drone surveys" ~ Drainage's "cctv drain survey"). Best first.
const EXACT_SCORE = 4;
const STRONG_SCORE = 2;

function scoreNiches(q, niches) {
  const qWords = q.split(" ");
  const scored = [];
  for (const niche of selectableNiches(niches)) {
    let best = 0;
    for (const term of nicheTerms(niche)) {
      if (term === q) best = Math.max(best, EXACT_SCORE);
      else if (term.startsWith(q) || containsPhrase(q, term)) best = Math.max(best, 3);
      else if (term.includes(q)) best = Math.max(best, STRONG_SCORE);
      else if (term.split(" ").some(tw => qWords.some(qw => isCloseSpelling(qw, tw)))) best = Math.max(best, 1);
    }
    if (best > 0) scored.push({ niche, score: best });
  }
  return scored.sort((a, b) => b.score - a.score || a.niche.name.localeCompare(b.niche.name));
}

// True when what was typed is exactly a niche name or synonym (then there's
// nothing to suggest).
export function isExactNiche(query, niches) {
  const q = tidyPhrase(query);
  return !!q && scoreNiches(q, niches).some(s => s.score === EXACT_SCORE);
}

// The niche Enter should pick in the Change box: only a clear match, never
// a single look-alike word. Null = Enter suggests what was typed instead.
export function clearNicheMatch(query, niches) {
  const q = tidyPhrase(query);
  if (!q) return null;
  const scored = scoreNiches(q, niches);
  if (scored[0] && scored[0].score >= STRONG_SCORE) return scored[0].niche;
  // "plumer" alone: a one-word query that looks like exactly one niche is still clear.
  return !q.includes(" ") && scored.length === 1 ? scored[0].niche : null;
}

// A suggested niche that clearly is an existing one ("plumbers" -> Plumbing)
// is used instead of saving a suggestion. Returns that niche or null.
export function existingNicheForSuggestion(text, niches) {
  const q = tidyPhrase(text);
  if (!q) return null;
  for (const niche of selectableNiches(niches)) {
    if (nicheTerms(niche).includes(q)) return niche;
  }
  const match = matchNicheFromKeywords([q], niches);
  return match ? match.niche : null;
}
